import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithRedirect,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import { setTokenRefreshHandlers } from '../utils/bbAxios';
import {
  setToken as setSocketToken,
  initSocketManager,
  disconnectAll as disconnectAllSockets,
} from '../utils/socketManager';
import { STORAGE_KEYS, WARNING_THRESHOLD_MS } from '../sessionConfig';
import { clearPermissions } from '../utils/permissionCache';
import { broadcastLogout } from '../utils/authSync';

/**
 * React context that holds the GCP session state.
 * Provided by GCPSessionWrapper in SessionManager and consumed by useAuth.
 */
export const GCPSessionContext = createContext(null);

/**
 * Initialize Firebase once using config from the backend.
 * Safe to call multiple times — only initializes on first call.
 */
const initFirebase = (config) => {
  if (getApps().length) {
    return getAuth();
  }
  const { projectId, apiKey, authDomain, tenantId } = config.oidcAuth;
  initializeApp({ apiKey, authDomain, projectId });
  const auth = getAuth();
  if (tenantId) {
    auth.tenantId = tenantId;
  }
  return auth;
};

const getTokenExpiryMs = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const formatCountdown = (msRemaining) => {
  if (msRemaining <= 0) return '0:00';
  const totalSeconds = Math.floor(msRemaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * GCP (Firebase) session management hook.
 *
 * Handles:
 * - Firebase initialization from backend config
 * - Auth state subscription via onAuthStateChanged
 * - Token storage in localStorage for bbAxios
 * - Proactive token refresh before expiry
 * - Session timeout dialog (~2 min before ID token expiry)
 * - Socket manager token sync
 */
export const useGCPSession = ({ config, loginRoute = '/bbweb/login', onSessionExpired } = {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expirationTimestamp, setExpirationTimestamp] = useState(null);
  const [countdownFormatted, setCountdownFormatted] = useState('');

  const authRef = useRef(null);
  const logoutRef = useRef(null);
  const dialogTimerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(dialogTimerRef.current);
    clearTimeout(refreshTimerRef.current);
    clearInterval(countdownIntervalRef.current);
  }, []);

  const refreshToken = useCallback(async () => {
    const auth = authRef.current;
    if (!auth?.currentUser) throw new Error('[GCPSession] No Firebase user available');
    const token = await auth.currentUser.getIdToken(true);
    localStorage.setItem(STORAGE_KEYS.GCP_TOKEN, token);
    setSocketToken(token);
    return token;
  }, []);

  const scheduleTimers = useCallback(
    (expMs) => {
      clearTimers();
      const now = Date.now();
      // Refresh proactively 90 seconds before the warning dialog would appear
      const msToRefresh = expMs - now - WARNING_THRESHOLD_MS - 90_000;
      const msToWarning = expMs - now - WARNING_THRESHOLD_MS;

      if (msToRefresh > 0) {
        refreshTimerRef.current = setTimeout(async () => {
          try {
            const newToken = await refreshToken();
            const newExpMs = getTokenExpiryMs(newToken);
            if (newExpMs) scheduleTimers(newExpMs);
          } catch (e) {
            console.error('[GCPSession] Proactive refresh failed:', e);
          }
        }, msToRefresh);
      }

      if (msToWarning > 0) {
        dialogTimerRef.current = setTimeout(() => {
          setExpirationTimestamp(expMs);
          setCountdownFormatted(formatCountdown(expMs - Date.now()));
          setIsDialogOpen(true);
          countdownIntervalRef.current = setInterval(() => {
            const remaining = expMs - Date.now();
            if (remaining <= 0) {
              clearInterval(countdownIntervalRef.current);
              setCountdownFormatted('0:00');
              setIsDialogOpen(false);
              onSessionExpired?.();
              logoutRef.current?.();
            } else {
              setCountdownFormatted(formatCountdown(remaining));
            }
          }, 1000);
        }, msToWarning);
      }
    },
    [clearTimers, refreshToken, onSessionExpired]
  );

  // Initialize Firebase and subscribe to auth state
  useEffect(() => {
    if (!config) return;

    const auth = initFirebase(config);
    authRef.current = auth;

    // Register GCP refresh handler so bbAxios can get fresh tokens on 401
    setTokenRefreshHandlers(null, null, 'gcp', refreshToken);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken(false);
          localStorage.setItem(STORAGE_KEYS.GCP_TOKEN, token);
          setIsAuthenticated(true);
          setUser({ email: firebaseUser.email, uid: firebaseUser.uid });
          const expMs = getTokenExpiryMs(token);
          if (expMs) scheduleTimers(expMs);
          initSocketManager();
          setSocketToken(token);
        } catch (e) {
          console.error('[GCPSession] Error processing auth state change:', e);
        }
      } else {
        localStorage.removeItem(STORAGE_KEYS.GCP_TOKEN);
        setIsAuthenticated(false);
        setUser(null);
        clearTimers();
        disconnectAllSockets();
      }
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
      clearTimers();
    };
    // scheduleTimers, clearTimers, and refreshToken are stable (no changing deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const continueSession = useCallback(async () => {
    setIsDialogOpen(false);
    clearTimers();
    try {
      const token = await refreshToken();
      const expMs = getTokenExpiryMs(token);
      if (expMs) scheduleTimers(expMs);
    } catch (e) {
      console.error('[GCPSession] Continue session refresh failed:', e);
    }
  }, [refreshToken, scheduleTimers, clearTimers]);

  const logout = useCallback(async () => {
    const auth = authRef.current;
    clearTimers();
    try {
      if (auth) await firebaseSignOut(auth);
    } catch (e) {
      console.error('[GCPSession] Logout error:', e);
    } finally {
      localStorage.removeItem(STORAGE_KEYS.GCP_TOKEN);
      clearPermissions();
      broadcastLogout('user_action');
      disconnectAllSockets();
      window.location.href = loginRoute;
    }
  }, [loginRoute, clearTimers]);

  logoutRef.current = logout;

  const signIn = useCallback(async (provider) => {
    const auth = authRef.current;
    if (!auth) throw new Error('[GCPSession] Firebase not initialized');
    await signInWithRedirect(auth, provider);
  }, []);

  const signInWithEmail = useCallback(async (email, password) => {
    const auth = authRef.current;
    if (!auth) throw new Error('[GCPSession] Firebase not initialized');
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  return {
    isLoading,
    isAuthenticated,
    user,
    isDialogOpen,
    expirationTimestamp,
    countdownFormatted,
    continueSession,
    logout,
    signIn,
    signInWithEmail,
  };
};
